import { Container, Sprite } from 'pixi.js';
import type { WorldHighlight } from '../bridge/AttentionBus';
import { BUILDING_DEFS, TICKS_PER_DAY, xOf, yOf } from '../sim/constants';
import { productionStall } from '../sim/systems/industry';
import type { Building, SimState } from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { HALF_W, tileTopY, vertexX, vertexY } from './iso';

const TUTORIAL_TINT = 0xf2c14e;
const PROBLEM_TINT = 0xe07a6a;
const MAX_HALOS = 48;
const MAX_BADGES = 48;

type BadgeKind = 'bolt' | 'boltHalf' | 'boltOff' | 'drop' | 'person' | 'sack' | 'crates';

interface Problem {
  badge: BadgeKind;
  halo: boolean;
}

/** Highest-severity problem wins the badge; halo only for hard stops. */
function buildingProblem(state: SimState, building: Building, day: number): Problem | null {
  const def = BUILDING_DEFS[building.type];
  if (building.outageUntilDay !== null && building.outageUntilDay > day) {
    return { badge: 'boltOff', halo: true };
  }
  if (def.powerDraw > 0 && !building.powered) return { badge: 'bolt', halo: true };
  if (def.waterPerDay !== undefined && !building.watered) return { badge: 'drop', halo: true };
  if (def.staffRequired > 0 && building.staffAssigned.length < def.staffRequired) {
    return { badge: 'person', halo: true };
  }
  // Brownout: running, but starved — a murmur, not an alarm.
  if (def.powerDraw > 0 && building.powered && building.powerEff < 1) {
    return { badge: 'boltHalf', halo: false };
  }
  if (def.produces && building.functional) {
    const stall = productionStall(state, building);
    if (stall !== null && stall.startsWith('no ')) return { badge: 'sack', halo: true };
    if (stall !== null) return { badge: 'crates', halo: true };
  }
  return null;
}

/**
 * World-space attention: yellow blinking halos on tutorial targets and
 * pale-red pulsing halos + cause badges on buildings in trouble. Halos render
 * under the buildings, badges above the roofs — two containers.
 *
 * Pulses run on real time so they stay alive while the game is paused; that
 * is when the player is most likely to be reading them.
 */
export class AttentionLayer {
  readonly haloContainer = new Container();
  readonly badgeContainer = new Container();

  private readonly tex: GrayboxTextures;
  private readonly halos: Sprite[] = [];
  private readonly badges: Sprite[] = [];
  /** Grace period: no nagging for the first day after a building appears. */
  private readonly firstSeen = new Map<number, number>();
  private primed = false;

  constructor(tex: GrayboxTextures) {
    this.tex = tex;
  }

  update(state: SimState, realSeconds: number, highlights: readonly WorldHighlight[]): void {
    let halosUsed = 0;
    let badgesUsed = 0;

    const halo = (index: number): Sprite => {
      let sprite = this.halos[index];
      if (!sprite) {
        sprite = new Sprite();
        this.halos.push(sprite);
        this.haloContainer.addChild(sprite);
      }
      sprite.visible = true;
      return sprite;
    };
    const badge = (index: number): Sprite => {
      let sprite = this.badges[index];
      if (!sprite) {
        sprite = new Sprite();
        sprite.anchor.set(0.5, 1);
        this.badges.push(sprite);
        this.badgeContainer.addChild(sprite);
      }
      sprite.visible = true;
      return sprite;
    };

    // Tutorial highlights: a hard on/off blink in the same rhythm as the DOM
    // outline (0.8s period), so both speak one visual language.
    const blinkAlpha = realSeconds % 0.8 < 0.4 ? 1 : 0.15;
    if (!state.meta.gameOver) {
      for (const highlight of highlights) {
        if (halosUsed >= MAX_HALOS) break;
        if (highlight.kind === 'building') {
          const building = state.buildings.find((b) => b.id === highlight.buildingId);
          if (!building) continue;
          const def = BUILDING_DEFS[building.type];
          const sprite = halo(halosUsed);
          halosUsed += 1;
          sprite.texture = this.tex.halo(def.w, def.h);
          sprite.tint = TUTORIAL_TINT;
          sprite.alpha = blinkAlpha;
          sprite.position.set(vertexX(building.x, building.y) - def.h * HALF_W, vertexY(building.x, building.y));
        } else {
          const x = xOf(highlight.col);
          const y = yOf(highlight.col);
          const depth = state.terrain.depth[highlight.col] ?? 0;
          const sprite = halo(halosUsed);
          halosUsed += 1;
          sprite.texture = this.tex.halo(1, 1);
          sprite.tint = TUTORIAL_TINT;
          sprite.alpha = blinkAlpha;
          sprite.position.set((x - y) * HALF_W - HALF_W, tileTopY(x, y, depth));
        }
      }
    }

    // Problem indicators.
    if (!state.meta.gameOver) {
      const tick = state.meta.tick;
      const day = Math.floor(tick / TICKS_PER_DAY);
      if (!this.primed) {
        // On the first frame (fresh game or loaded save) everything counts as
        // old, so existing problems show immediately.
        this.primed = true;
        for (const building of state.buildings) this.firstSeen.set(building.id, tick - TICKS_PER_DAY);
      }
      const seen = new Set<number>();
      for (const building of state.buildings) {
        seen.add(building.id);
        const first = this.firstSeen.get(building.id);
        if (first === undefined) {
          this.firstSeen.set(building.id, tick);
          continue;
        }
        if (tick - first < TICKS_PER_DAY) continue;
        const problem = buildingProblem(state, building, day);
        if (!problem) continue;
        const def = BUILDING_DEFS[building.type];
        if (problem.halo && halosUsed < MAX_HALOS) {
          const sprite = halo(halosUsed);
          halosUsed += 1;
          sprite.texture = this.tex.halo(def.w, def.h);
          sprite.tint = PROBLEM_TINT;
          // A slow murmur, phase-offset per building so a struggling district
          // shimmers instead of strobing in lockstep.
          sprite.alpha = 0.3 + 0.2 * Math.sin(realSeconds * 3 + building.id);
          sprite.position.set(vertexX(building.x, building.y) - def.h * HALF_W, vertexY(building.x, building.y));
        }
        if (badgesUsed < MAX_BADGES) {
          const sprite = badge(badgesUsed);
          badgesUsed += 1;
          sprite.texture = this.tex.badge(problem.badge);
          sprite.alpha = 0.95;
          const cx = vertexX(building.x, building.y) + ((def.w - def.h) * HALF_W) / 2;
          const cy = vertexY(building.x, building.y) - 34 - 2 * Math.sin(realSeconds * 2 + building.id);
          sprite.position.set(cx, cy);
        }
      }
      for (const id of this.firstSeen.keys()) {
        if (!seen.has(id)) this.firstSeen.delete(id);
      }
    }

    for (let index = halosUsed; index < this.halos.length; index += 1) this.halos[index]!.visible = false;
    for (let index = badgesUsed; index < this.badges.length; index += 1) this.badges[index]!.visible = false;
  }
}
